import React from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { StatusCheckNode, XMLCard } from 'pages/Chat/MarkdownRender';
import { styled } from '@mui/material/styles';
import { PropsTable } from 'pages/Chat/QuantModeTable';

const WarningBox = styled(Box)(({ theme }) => ({
	backgroundColor: '#EBEBEB',
	padding: '12px 20px',
	borderRadius: 7,
	marginBottom: theme.spacing(2),
	display: 'flex',
	alignItems: 'center',
	fontWeight: 500,
	fontSize: 14,
	borderLeftWidth: 5,
	borderLeftStyle: 'solid',
	color: '#333',
	borderLeftColor: '#1C4ED8',
}));

const StyledButton = styled(Button)(({ theme }) => ({
	borderRadius: '12px', // rounded corners
	border: '1px solid #E0E0E0', // light gray border
	backgroundColor: '#fff', // white background
	color: '#000', // black text
	textTransform: 'none', // keep original casing
	fontSize: '18px',
	fontWeight: 400,
	padding: '8px 24px',
	boxShadow: 'none',
	'&:hover': {
		backgroundColor: '#f5f5f5', // subtle hover effect
		boxShadow: 'none',
	},
}));

const SectionCard: React.FC<{ title?: string; children: React.ReactNode }> = ({
	title,
	children,
}) => (
	<Card variant="outlined" sx={{ margin: 0, borderWidth: 0, padding: 0 }}>
		<CardContent sx={{ padding: '16px 8px' }}>
			{title && (
				<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
					{title}
				</Typography>
			)}
			{children}
		</CardContent>
	</Card>
);

const CardRenderer: React.FC<{ card: XMLCard }> = ({ card }) => (
	<SectionCard title={card.title}>
		{!!card.props.length && <PropsTable propsList={card.props} />}
		{!!card.sections.length && (
			<Box>
				{card.sections.map((sec, i) => (
					<SectionCard key={`sec-${i}`} title={sec.title}>
						{!!sec.props.length && (
							<PropsTable propsList={sec.props} />
						)}
						{!!sec.notes.length && (
							<Box component="ul" sx={{ pl: 3, m: 0 }}>
								{sec.notes.map((n, idx) => (
									<Typography
										key={idx}
										component="li"
										variant="body2"
									>
										{n}
									</Typography>
								))}
							</Box>
						)}
					</SectionCard>
				))}
			</Box>
		)}
		{!!card.notes.length && (
			<>
				{card.notes.map((n, i) => (
					<WarningBox key={`${n} + ${i}`}>
						<Typography key={i} variant="body2">
							{n}
						</Typography>
					</WarningBox>
				))}
			</>
		)}
	</SectionCard>
);

export const StatusCheck: React.FC<{ node: StatusCheckNode }> = ({ node }) => {
	return (
		<>
			{node.cards.map((c, i) => (
				<CardRenderer key={`analysis-${i}`} card={c} />
			))}
		</>
	);
};
